import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Search, Users, Mail, Sparkles, Instagram, Youtube, Twitter, MessageSquare, Facebook, Globe, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SocialModal from '../components/SocialModal';
import './HomePage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'India', 'Brazil', 'Mexico', 'Japan', 'Nigeria', 'Egypt', 'South Africa', 'Ghana', 'Kenya', 'Morocco'];
const SOCIAL_PLATFORMS = ['Instagram', 'YouTube', 'TikTok', 'X (Twitter)', 'Facebook', 'Pinterest', 'LinkedIn', 'Snapchat'];
const BUDGET_RANGES = ['$500 - $1,000', '$1,000 - $5,000', '$5,000 - $10,000', '$10,000 - $25,000', '$25,000 - $50,000', '$50,000+'];

const PublicHomePage = () => {
  const navigate = useNavigate();
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedInfluencers, setSelectedInfluencers] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [singleContactModalOpen, setSingleContactModalOpen] = useState(false);
  const [currentInfluencer, setCurrentInfluencer] = useState(null);
  const [socialModal, setSocialModal] = useState({ open: false, platform: '', handle: '' });
  const [contactForm, setContactForm] = useState({
    user_name: '',
    user_email: '',
    phone_number: '',
    company_or_individual: 'company',
    message: '',
    budget_range: '',
    social_platform: '',
    target_country: '',
    target_state: '',
    target_city: '',
    target_zip: '',
    ad_targeting_info: '',
    estimated_reach: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchInfluencers();
  }, [searchQuery, selectedCountry, selectedState, selectedCity]);

  const fetchInfluencers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/influencers/public`, {
        params: {
          search: searchQuery || undefined,
          country: selectedCountry || undefined,
          state: selectedState || undefined,
          city: selectedCity || undefined,
          limit: 8
        }
      });
      setInfluencers(response.data);
    } catch (error) {
      console.error('Error fetching influencers:', error);
      toast.error('Failed to load influencers');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectInfluencer = (influencerId) => {
    setSelectedInfluencers(prev => {
      if (prev.includes(influencerId)) {
        return prev.filter(id => id !== influencerId);
      } else {
        return [...prev, influencerId];
      }
    });
  };

  const handleSingleContact = (influencer) => {
    setCurrentInfluencer(influencer);
    setSingleContactModalOpen(true);
  };

  const handleSocialClick = (platform, handle) => {
    setSocialModal({ open: true, platform, handle });
  };

  const handleSubmitSingleContact = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/api/contact/public`, {
        influencer_ids: [currentInfluencer.id],
        ...contactForm
      });
      
      toast.success('Your request has been sent! We\'ll connect you with the influencer soon.');
      setSingleContactModalOpen(false);
      resetContactForm();
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error('Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitMultiContact = async (e) => {
    e.preventDefault();
    
    if (selectedInfluencers.length === 0) {
      toast.error('Please select at least one influencer');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/api/contact/public`, {
        influencer_ids: selectedInfluencers,
        ...contactForm
      });
      
      toast.success('Your request has been sent! We\'ll connect you with the influencers soon.');
      setContactModalOpen(false);
      setSelectedInfluencers([]);
      resetContactForm();
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error('Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const resetContactForm = () => {
    setContactForm({
      user_name: '',
      user_email: '',
      phone_number: '',
      company_or_individual: 'company',
      message: '',
      budget_range: '',
      social_platform: '',
      target_country: '',
      target_state: '',
      target_city: '',
      target_zip: '',
      ad_targeting_info: '',
      estimated_reach: ''
    });
  };

  const renderContactForm = (onSubmit, isMulti = false) => (
    <form onSubmit={onSubmit} className="contact-form">
      <div className="form-row">
        <div className="form-group">
          <Label htmlFor="user_name">Your Name *</Label>
          <Input
            id="user_name"
            value={contactForm.user_name}
            onChange={(e) => setContactForm({...contactForm, user_name: e.target.value})}
            required
          />
        </div>
        <div className="form-group">
          <Label htmlFor="user_email">Email *</Label>
          <Input
            id="user_email"
            type="email"
            value={contactForm.user_email}
            onChange={(e) => setContactForm({...contactForm, user_email: e.target.value})}
            required
          />
        </div>
      </div>

      <div className="form-group">
        <Label htmlFor="phone_number">Phone Number *</Label>
        <Input
          id="phone_number"
          type="tel"
          value={contactForm.phone_number}
          onChange={(e) => setContactForm({...contactForm, phone_number: e.target.value})}
          placeholder="+1 (555) 123-4567"
          required
        />
      </div>

      <div className="form-group">
        <Label>Type *</Label>
        <RadioGroup value={contactForm.company_or_individual} onValueChange={(value) => setContactForm({...contactForm, company_or_individual: value})}>
          <div className="radio-item">
            <RadioGroupItem value="company" id="company" />
            <Label htmlFor="company" className="radio-label">Company</Label>
          </div>
          <div className="radio-item">
            <RadioGroupItem value="individual" id="individual" />
            <Label htmlFor="individual" className="radio-label">Individual</Label>
          </div>
        </RadioGroup>
      </div>

      <div className="form-row">
        <div className="form-group">
          <Label htmlFor="budget_range">Budget Range *</Label>
          <Select value={contactForm.budget_range} onValueChange={(value) => setContactForm({...contactForm, budget_range: value})} required>
            <SelectTrigger><SelectValue placeholder="Select budget" /></SelectTrigger>
            <SelectContent>
              {BUDGET_RANGES.map(range => <SelectItem key={range} value={range}>{range}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="form-group">
          <Label htmlFor="social_platform">Platform for Campaign *</Label>
          <Select value={contactForm.social_platform} onValueChange={(value) => setContactForm({...contactForm, social_platform: value})} required>
            <SelectTrigger><SelectValue placeholder="Select platform" /></SelectTrigger>
            <SelectContent>
              {SOCIAL_PLATFORMS.map(platform => <SelectItem key={platform} value={platform}>{platform}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="form-section-title">Target Location</div>
      <div className="form-row">
        <div className="form-group">
          <Label htmlFor="target_country">Country</Label>
          <Select value={contactForm.target_country} onValueChange={(value) => setContactForm({...contactForm, target_country: value})}>
            <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
            <SelectContent>
              {COUNTRIES.map(country => <SelectItem key={country} value={country}>{country}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="form-group">
          <Label htmlFor="target_state">State/Province</Label>
          <Input
            id="target_state"
            value={contactForm.target_state}
            onChange={(e) => setContactForm({...contactForm, target_state: e.target.value})}
            placeholder="e.g., California"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <Label htmlFor="target_city">City</Label>
          <Input
            id="target_city"
            value={contactForm.target_city}
            onChange={(e) => setContactForm({...contactForm, target_city: e.target.value})}
            placeholder="e.g., Los Angeles"
          />
        </div>
        <div className="form-group">
          <Label htmlFor="target_zip">ZIP Code</Label>
          <Input
            id="target_zip"
            value={contactForm.target_zip}
            onChange={(e) => setContactForm({...contactForm, target_zip: e.target.value})}
            placeholder="e.g., 90210"
          />
        </div>
      </div>

      <div className="form-group">
        <Label htmlFor="estimated_reach">Estimated Reach</Label>
        <Input
          id="estimated_reach"
          value={contactForm.estimated_reach}
          onChange={(e) => setContactForm({...contactForm, estimated_reach: e.target.value})}
          placeholder="e.g., 100,000 - 500,000"
        />
      </div>

      <div className="form-group">
        <Label htmlFor="ad_targeting_info">Additional Ad Targeting Info</Label>
        <Textarea
          id="ad_targeting_info"
          value={contactForm.ad_targeting_info}
          onChange={(e) => setContactForm({...contactForm, ad_targeting_info: e.target.value})}
          placeholder="Demographics, interests, behaviors, etc."
          rows={3}
        />
      </div>

      <div className="form-group">
        <Label htmlFor="message">Campaign Message *</Label>
        <Textarea
          id="message"
          placeholder="Tell us about your campaign goals and requirements..."
          value={contactForm.message}
          onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
          rows={4}
          required
        />
      </div>

      <Button type="submit" disabled={submitting} className="submit-btn">
        {submitting ? 'Sending...' : 'Send Request'}
      </Button>
    </form>
  );

  return (
    <div className="home-container">
      <header className="header">
        <div className="header-content">
          <div className="logo" data-testid="logo">
            <Sparkles className="logo-icon" />
            <span>1000hints</span>
          </div>
          <div className="header-actions">
            <Button onClick={() => navigate('/login')} variant="ghost" className="login-btn">Login</Button>
            <Button onClick={() => navigate('/register')} className="register-btn">
              <Users className="btn-icon" />
              Join Now
            </Button>
          </div>
        </div>
      </header>

      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Discover Top Influencers</h1>
          <p className="hero-subtitle">Your trusted agency connecting brands with the best creators across Instagram, YouTube, TikTok, and more</p>
          
          {/* Location Search */}
          <div className="location-search">
            <MapPin className="location-icon" />
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="location-select"><SelectValue placeholder="Select Country" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {COUNTRIES.map(country => <SelectItem key={country} value={country}>{country}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              placeholder="State"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="location-input"
            />
            <Input
              placeholder="City"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="location-input"
            />
          </div>

          <div className="search-container">
            <Search className="search-icon" />
            <Input
              type="text"
              placeholder="Search influencers by name, category, or niche..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="multi-select-note">
            <Users size={18} className="note-icon" />
            <p>Select multiple influencers and contact them simultaneously</p>
          </div>
        </div>
        <div className="hero-bg"></div>
      </section>

      {selectedInfluencers.length > 0 && (
        <div className="selected-bar">
          <span>{selectedInfluencers.length} influencer(s) selected</span>
          <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
            <DialogTrigger asChild>
              <Button className="contact-btn"><Mail className="btn-icon" />Contact Selected</Button>
            </DialogTrigger>
            <DialogContent className="contact-modal large-modal">
              <DialogHeader>
                <DialogTitle>Contact {selectedInfluencers.length} Influencer(s)</DialogTitle>
                <DialogDescription>Fill out the form below. We'll forward your request and connect you directly.</DialogDescription>
              </DialogHeader>
              {renderContactForm(handleSubmitMultiContact, true)}
            </DialogContent>
          </Dialog>
        </div>
      )}

      <section className="influencers-section">
        <div className="section-content">
          {loading ? (
            <div className="loading-state"><div className="loader"></div><p>Loading influencers...</p></div>
          ) : influencers.length === 0 ? (
            <div className="empty-state"><Users size={48} /><p>No influencers found</p></div>
          ) : (
            <>
              <div className="influencers-grid">
                {influencers.map((influencer) => (
                  <div key={influencer.id} className="influencer-card">
                    <div className="card-select">
                      <Checkbox checked={selectedInfluencers.includes(influencer.id)} onCheckedChange={() => handleSelectInfluencer(influencer.id)} />
                    </div>
                    <div className="card-image" onClick={() => navigate(`/influencer/${influencer.id}`)}>
                      {influencer.profile_image_url ? <img src={influencer.profile_image_url} alt={influencer.name} /> : <div className="placeholder-image"><Users size={48} /></div>}
                    </div>
                    <div className="card-content">
                      <h3 className="card-name">{influencer.name}</h3>
                      {(influencer.city || influencer.country) && (
                        <div className="card-location">
                          <MapPin size={14} />
                          <span>{[influencer.city, influencer.country].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                      <p className="card-bio">{influencer.bio.substring(0, 100)}...</p>
                      <div className="card-categories">{influencer.categories.map((cat, idx) => <span key={idx} className="category-tag">{cat}</span>)}</div>
                      <div className="card-socials">
                        {influencer.social_handles.instagram && <button onClick={() => handleSocialClick('instagram', influencer.social_handles.instagram)} className="social-btn"><Instagram size={18} /></button>}
                        {influencer.social_handles.youtube && <button onClick={() => handleSocialClick('youtube', influencer.social_handles.youtube)} className="social-btn"><Youtube size={18} /></button>}
                        {influencer.social_handles.x && <button onClick={() => handleSocialClick('x', influencer.social_handles.x)} className="social-btn"><Twitter size={18} /></button>}
                      </div>
                      <div className="card-footer">
                        <span className={`availability ${influencer.availability ? 'available' : 'unavailable'}`}>{influencer.availability ? '● Available' : '● Unavailable'}</span>
                        <Button onClick={() => handleSingleContact(influencer)} className="connect-btn"><MessageSquare size={16} />Connect</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="business-assurance-box">
                <Sparkles className="assurance-icon" size={24} />
                <p>We ensure smooth execution and optimal results from your chosen influencers. Maximize your business with top talent for products, events, and promotions.</p>
              </div>
            </>
          )}
        </div>
      </section>

      <Dialog open={singleContactModalOpen} onOpenChange={setSingleContactModalOpen}>
        <DialogContent className="contact-modal large-modal">
          <DialogHeader>
            <DialogTitle>Connect with {currentInfluencer?.name}</DialogTitle>
            <DialogDescription>As your trusted agency partner, we'll forward your inquiry and help facilitate the connection.</DialogDescription>
          </DialogHeader>
          {renderContactForm(handleSubmitSingleContact, false)}
        </DialogContent>
      </Dialog>

      <SocialModal open={socialModal.open} onClose={() => setSocialModal({ open: false, platform: '', handle: '' })} platform={socialModal.platform} handle={socialModal.handle} />
    </div>
  );
};

export default PublicHomePage;